import type sv from './sv'

type DeepString<T> = T extends string
  ? string
  : { readonly [K in keyof T]: DeepString<T[K]> }

const da: DeepString<typeof sv> = {
  title: 'Opret din App',
  description:
    'Byg din app på Convex SaaS, udforsk dokumentationen og påbegynd din rejse.',
  nav: {
    dashboard: 'Oversigt',
    jobs: 'Stillinger',
    candidates: 'Kandidater',
    questionBank: 'Spørgsmålsbank',
    templates: 'Skabeloner',
    settings: 'Indstillinger',
    billing: 'Fakturering',
    documentation: 'Dokumentation',
    personalAccount: 'Personlig konto',
    topUpBalance: 'Påfyld saldo',
    theme: 'Tema',
    language: 'Sprog',
    logOut: 'Log ud',
  },
  header: {
    dashboard: {
      title: 'Oversigt',
      description: 'Administrer dine apps og se dit forbrug.',
    },
    jobs: {
      title: 'Stillinger',
      description: 'Administrer dine stillingsopslag og interviewskabeloner.',
    },
    settings: {
      title: 'Indstillinger',
      description: 'Administrer dine kontoindstillinger.',
    },
    billing: {
      title: 'Fakturering',
      description: 'Administrer din saldo og køb interviews.',
    },
    organisation: {
      title: 'Organisation',
      description: 'Administrer organisationsindstillinger.',
    },
    team: {
      title: 'Team',
      description: 'Administrer teammedlemmer og adgange.',
    },
    interviews: {
      title: 'Interviewrapport',
      description: 'Se kandidatens interviewresultater og AI-analyse.',
    },
    questionBank: {
      title: 'Spørgsmålsbank',
      description: 'Gem og genbrug interviewspørgsmål på tværs af stillinger.',
    },
    templates: {
      title: 'Jobskabeloner',
      description: 'Gem og genbrug jobkonfigurationer som skabeloner.',
    },
    candidates: {
      title: 'Kandidater',
      description: 'Søg og filtrer kandidater på tværs af alle stillinger.',
    },
  },
  auth: {
    login: {
      title: 'Fortsæt til Convex SaaS',
      subtitle: 'Velkommen tilbage! Log ind for at fortsætte.',
      emailLabel: 'E-mail',
      emailPlaceholder: 'E-mail',
      emailInvalid: 'E-mailadressen er ikke gyldig.',
      continueWithEmail: 'Fortsæt med e-mail',
      termsText: 'Ved at klikke på fortsæt accepterer du vores',
      termsOfService: 'Vilkår for brug',
      and: 'og',
      privacyPolicy: 'Privatlivspolitik.',
      ssoSignIn: 'Log ind med SSO',
      ssoOr: 'eller',
      ssoRedirecting: 'Omdirigerer til din identitetsudbyder…',
      ssoNotConfigured: 'SSO er ikke konfigureret for dette e-maildomæne.',
    },
    verify: {
      title: 'Tjek din indbakke!',
      subtitle:
        'Vi har netop sendt et midlertidigt kodeord til dig via e-mail. Indtast det nedenfor.',
      codePlaceholder: 'Kode',
      codeLabel: 'Kode',
      codeMinLength: 'Koden skal være mindst 8 tegn.',
      continue: 'Fortsæt',
      didNotReceive: 'Modtog du ikke koden?',
      requestNewCode: 'Anmod om ny kode',
      codeSent: 'En ny kode er sendt.',
      invalidCode: 'Ugyldig eller udløbet kode.',
    },
  },
  onboarding: {
    username: {
      title: 'Velkommen!',
      subtitle: 'Lad os starte med at konfigurere din profil.',
      organizationNamePlaceholder: 'Organisationsnavn',
      organizationNameLabel: 'Organisationsnavn',
      firstNamePlaceholder: 'Fornavn',
      firstNameLabel: 'Fornavn',
      lastNamePlaceholder: 'Efternavn',
      lastNameLabel: 'Efternavn',
      usernamePlaceholder: 'Brugernavn',
      usernameLabel: 'Brugernavn',
      continue: 'Fortsæt',
      hint: 'Du kan opdatere dit brugernavn når som helst fra dine kontoindstillinger.',
    },
  },
  jobs: {
    createJob: 'Opret stilling',
    noJobs: 'Ingen stillinger endnu',
    noJobsDescription: 'Opret din første stilling for at komme i gang.',
    statusDraft: 'Kladde',
    statusActive: 'Aktiv',
    statusPaused: 'Sat på pause',
    duplicate: 'Dupliker',
    pause: 'Sæt på pause',
    pauseConfirm: 'Er du sikker?',
    delete: 'Slet',
    deleteConfirm: 'Er du sikker?',
    form: {
      titleLabel: 'Titel',
      titlePlaceholder: 'F.eks. Backend-udvikler',
      descriptionLabel: 'Beskrivelse',
      descriptionPlaceholder: 'Beskriv rollen og hvad I søger...',
      languageLabel: 'Interviewsprog',
      languageSv: 'Svensk',
      languageEn: 'Engelsk',
      languageNo: 'Norsk',
      languageDa: 'Dansk',
      languageFi: 'Finsk',
      languageBoth: 'Svensk & Engelsk',
      allowedModalitiesLabel: 'Tilladte modaliteter',
      videoLabel: 'Video',
      videoRequired: 'Obligatorisk',
      videoOptional: 'Valgfri',
      videoDisabled: 'Deaktiveret',
      audioLabel: 'Lyd',
      audioRequired: 'Obligatorisk',
      audioDisabled: 'Deaktiveret',
      chatLabel: 'Chat',
      chatEnabled: 'Aktiveret',
      chatDisabled: 'Deaktiveret',
      expiryDaysLabel: 'Lænkens gyldighedsperiode (dage)',
      interviewCountLabel: 'Antal interviews',
      topNLabel: 'Antal topkandidater at fremhæve',
      save: 'Gem',
      create: 'Opret stilling',
      cancel: 'Annuller',
    },
    tabs: {
      overview: 'Oversigt',
      questions: 'Spørgsmål',
      screener: 'Screener',
      additionalInfo: 'Yderligere info',
      modality: 'Modalitet',
      voice: 'Stemme',
      candidates: 'Kandidater',
      candidateActions: 'Handlinger',
      report: 'Rapport',
      settings: 'Indstillinger',
    },
    overview: {
      name: 'Navn',
      email: 'E-mail',
      status: 'Status',
      score: 'Point',
      report: 'Rapport',
      noScore: '—',
      viewReport: 'Vis rapport',
      shortlist: 'Shortlist',
      shortlistedOnly: 'Kun shortlistede',
      shareShortlist: 'Del shortlist',
      copyLink: 'Kopiér link',
      linkCopied: 'Link kopieret!',
    },
    shortlist: {
      title: 'Shortlistede kandidater',
      jobLabel: 'Stilling',
      name: 'Navn',
      status: 'Status',
      score: 'Point',
      noScore: '—',
      empty: 'Ingen kandidater er shortlistet endnu.',
    },
    launch: {
      button: 'Lancér stilling',
      topUpAndLaunch: 'Påfyld & lancér',
      active: 'Aktiv',
      insufficientBalance:
        'Du skal bruge mindst {{required}} DKK for at lancere. Nuværende saldo: {{balance}} DKK',
    },
    preview: {
      button: 'Forhåndsvis interview',
      banner: 'Dette er en forhåndsvisning — ingen kreditter trækkes.',
      loading: 'Opretter forhåndsvisning...',
      error: 'Kunne ikke starte forhåndsvisning. Prøv igen.',
      close: 'Luk',
      completed: 'Forhåndsvisning afsluttet.',
      screener: {
        title: 'Forhåndstilmeldingsspørgsmål',
        continue: 'Fortsæt',
      },
      additionalInfo: {
        title: 'Yderligere information',
        required: '*',
        continue: 'Fortsæt',
      },
      briefing: {
        aboutToStart: 'Du er ved at starte et AI-ledet videointerview.',
        expectedDuration: 'Forventet varighed: cirka {{minutes}} minutter.',
        quietEnvironment:
          'Sørg for, at du befinder dig i et roligt miljø med en fungerende mikrofon.',
        consentText:
          'Jeg samtykker til at blive optaget under dette interview.',
        termsOfService: 'Vilkår for brug',
        privacyPolicy: 'Privatlivspolitik',
        starting: 'Starter...',
        ready: 'Jeg er klar',
      },
      disqualified: {
        title: 'Tak for din interesse',
        body: 'Desværre opfylder du ikke kravene til denne stilling.',
      },
      room: {
        connecting: 'Intervieweren forbinder...',
      },
    },
    voice: {
      loading: 'Indlæser stemmer...',
      noResults: 'Ingen stemmer matcher dine filtre.',
      filterLanguage: 'Sprog',
      filterAccent: 'Accent',
      filterCategory: 'Kategori',
      filterGender: 'Køn',
      filterAge: 'Alder',
    },
    screener: {
      questionPlaceholder: 'F.eks. Har du gyldigt kørekort?',
      optionPlaceholder: 'Mulighed',
      singleSelect: 'Enkeltvalg',
      multiChoice: 'Flervalg',
      markDisqualifying: 'Markér som diskvalificerende',
      disqualifyingHint:
        'Afkryds en mulighed for at diskvalificere kandidater, der vælger den.',
      addOption: 'Tilføj mulighed',
      removeOption: 'Fjern mulighed',
      addQuestion: 'Tilføj spørgsmål',
      remove: 'Fjern',
      save: 'Gem screener',
      saving: 'Gemmer...',
      saved: 'Gemt',
      empty:
        'Ingen screenerspørgsmål endnu. Tilføj dit første spørgsmål nedenfor.',
      drag: 'Træk for at ændre rækkefølge',
    },
    additionalInfo: {
      labelPlaceholder: 'F.eks. Portfolio-URL',
      placeholderPlaceholder: 'F.eks. https://...',
      typeText: 'Tekst',
      typeNumber: 'Tal',
      typeDate: 'Dato',
      typeUrl: 'URL',
      typeEmail: 'E-mail',
      requiredLabel: 'Obligatorisk',
      addField: 'Tilføj felt',
      remove: 'Fjern',
      save: 'Gem felter',
      saving: 'Gemmer...',
      saved: 'Gemt',
      empty: 'Ingen felter endnu. Tilføj dit første felt nedenfor.',
      drag: 'Træk for at ændre rækkefølge',
    },
    questions: {
      textPlaceholder: 'Skriv dit interviewspørgsmål...',
      competencyPlaceholder: 'Kompetence (valgfrit)',
      followUpPlaceholder: 'Opfølgningsspørgsmål...',
      addQuestion: 'Tilføj spørgsmål',
      addFollowUp: 'Tilføj opfølgning',
      showFollowUps: 'Opfølgningsspørgsmål ({{count}})',
      hideFollowUps: 'Skjul opfølgningsspørgsmål',
      remove: 'Fjern',
      removeFollowUp: 'Fjern opfølgning',
      saveToBank: 'Gem til bank',
      addFromBank: 'Tilføj fra bank',
      save: 'Gem spørgsmål',
      saving: 'Gemmer...',
      saved: 'Gemt',
      empty: 'Ingen spørgsmål endnu. Tilføj dit første spørgsmål nedenfor.',
      drag: 'Træk for at ændre rækkefølge',
    },
    locked: {
      notice:
        'Dette kan ikke ændres, mens stillingen er aktiv. Sæt stillingen på pause for at foretage ændringer.',
    },
    generate: {
      title: 'Generer med AI',
      tabText: 'Indsæt tekst',
      tabUrl: 'URL',
      tabPdf: 'PDF',
      textPlaceholder: 'Indsæt et stillingsopslag her...',
      urlPlaceholder: 'https://example.com/stillingsopslag',
      pdfLabel: 'Vælg PDF',
      pdfSelected: '{{name}}',
      generate: 'Generer',
      generating: 'Genererer...',
      addToJob: 'Tilføj',
      discard: 'Kassér',
      addAll: 'Tilføj alle',
      discardAll: 'Kassér alle',
      error: 'Kunne ikke generere spørgsmål. Prøv igen.',
    },
    settings: {
      reminderCadenceTitle: 'Påmindelsesplan',
      reminderCadenceDescription:
        'Angiv hvilke dage efter invitationen en påmindelse skal sendes. Lad stå tomt for standardplanen (halvvejs, dagen før, sidste dag).',
      reminderCadenceDefault:
        'Standardplan bruges (halvvejs, dagen før, sidste dag).',
      reminderDay: 'Dag {{day}}',
      addReminderDay: 'Tilføj dag',
      removeReminderDay: 'Fjern påmindelsesdag',
      reminderDayPlaceholder: 'Dag (f.eks. 2)',
      competencyWeightsTitle: 'Kompetencevægte',
      competencyWeightsDescription:
        'Tildel en relativ vægt for hver kompetence ved beregning af gennemsnitlig point. Standardvægt er 1 (ens vægtning).',
      competencyWeightsNoCompetencies:
        'Ingen kompetencer defineret endnu. Tilføj kompetencer i dine interviewspørgsmål først.',
      competencyWeightsLocked:
        'Vægte kan ikke ændres, mens stillingen er aktiv.',
      introMessageTitle: 'Introbeskrivelse',
      introMessageDescription:
        'Vises for kandidater på briefing-skærmen, før interviewet starter. Lad stå tomt for at bruge standardteksten.',
      introMessagePlaceholder: 'Tilføj en personlig introduktionsbesked…',
      outroMessageTitle: 'Outrobeskrivelse',
      outroMessageDescription:
        'Vises for kandidater på slutskærmen, efter interviewet er afsluttet. Lad stå tomt for at bruge standardteksten.',
      outroMessagePlaceholder: 'Tilføj en personlig afslutningsbesked…',
      translationsTitle: 'Oversættelser',
      translationsDescription:
        'Angiv jobindhold på alle interviewsprog. Brug oversættelsesknappen til at generere et udkast, gennemgå og gem det derefter.',
      candidateFeedbackTitle: 'Kandidattilbagemelding',
      candidateFeedbackDescription:
        'Vis kandidater deres personlige kompetencepoint efter afsluttet interview.',
      candidateFeedbackEnabled: 'Vis tilbagemelding til kandidater',
    },
    translations: {
      generating: 'Genererer…',
      translateFrom: 'Oversæt fra {{language}}',
      draftHint:
        'AI-udkast — gennemgå og rediger, klik derefter uden for for at gemme.',
      swedish: 'Svensk',
      english: 'Engelsk',
    },
  },
  questionBank: {
    addQuestion: 'Tilføj spørgsmål',
    textPlaceholder: 'Skriv dit interviewspørgsmål...',
    competencyPlaceholder: 'Kompetence (valgfrit)',
    save: 'Gem',
    empty: 'Ingen spørgsmål endnu. Tilføj dit første spørgsmål nedenfor.',
    delete: 'Slet',
    deleteConfirm: 'Er du sikker?',
  },
  interviews: {
    header: {
      title: 'Interviewrapport',
      description: 'Se kandidatens interviewresultater og AI-analyse.',
    },
    candidates: {
      noInterviews: 'Ingen interviews endnu.',
      statusPending: 'Afventer',
      statusInProgress: 'I gang',
      statusCompleted: 'Afsluttet',
      statusFailed: 'Mislykkedes',
      viewReport: 'Vis rapport',
      duration: '{{min}}m {{sec}}s',
      capacity: '{{used}} / {{total}} pladser brugt',
      increaseCapacity: 'Øg kapacitet',
      increaseCapacityTitle: 'Øg interviewkapacitet',
      increaseCapacityDescription:
        'Hver ekstra plads koster {{cost}} kreditter. Din nuværende saldo er {{balance}} kreditter.',
      additionalSlots: 'Ekstra pladser',
      increaseCapacityConfirm: 'Bekræft',
      increaseCapacityCancel: 'Annuller',
      increaseCapacityInsufficientBalance:
        'Utilstrækkelig saldo. Du har brug for {{required}} kreditter, men har kun {{balance}}.',
    },
    candidateActions: {
      calendlyUrlLabel: 'Calendly-link',
      calendlyUrlDescription:
        'Indsæt dit Calendly-link for at sende det til kandidater, der går videre.',
      saveCalendlyUrl: 'Gem',
      noCandidates: 'Ingen kandidater endnu.',
      statusInvited: 'Inviteret',
      statusStarted: 'Påbegyndt',
      statusCompleted: 'Afsluttet',
      statusExpired: 'Udløbet',
      statusDisqualified: 'Diskvalificeret',
      statusRejected: 'Afvist',
      statusAdvanced: 'Sendt videre',
      reject: 'Afvis',
      advance: 'Send videre',
      reTake: 'Tillad nyt forsøg',
      rejectDialogTitle: 'Send afslagsmail',
      rejectDialogDescription:
        'Dette sender en afslagsmail til kandidaten. Du kan tilføje en valgfri personlig besked nedenfor.',
      rejectCustomMessagePlaceholder: 'Valgfri personlig besked...',
      rejectConfirm: 'Send afslag',
      advanceConfirmTitle: 'Send kandidat videre',
      advanceConfirmDescription:
        'Dette sender et Calendly-link til kandidaten til booking af et interview.',
      advanceConfirm: 'Send videre',
      reTakeConfirmTitle: 'Tillad nyt forsøg',
      reTakeConfirmDescription:
        'Dette nulstiller kandidatens invitationslink og forlænger fristen. Kandidaten får et nyt link.',
      reTakeConfirm: 'Tillad nyt forsøg',
      cancel: 'Annuller',
      selected: '{{count}} valgt',
      bulkReject: 'Afvis valgte',
      bulkAdvance: 'Send valgte videre',
      bulkSendReminder: 'Send påmindelse',
      bulkRejectDialogTitle: 'Afvis valgte kandidater',
      bulkRejectDialogDescription:
        'Dette sender en afslagsmail til {{count}} kandidat(er). Du kan tilføje en valgfri personlig besked nedenfor.',
      bulkRejectConfirm: 'Afvis alle',
    },
    report: {
      back: 'Tilbage til stilling',
      summary: 'Sammendrag',
      noSummary: 'Analyse i gang...',
      competencies: 'Kompetencevurdering',
      noScores: 'Ingen point endnu.',
      rationale: 'Begrundelse',
      quotes: 'Citater',
      noQuotes: 'Ingen citater.',
      transcript: 'Transskription',
      noTranscript: 'Ingen transskription tilgængelig.',
      duration: 'Varighed',
      completedAt: 'Afsluttet',
      shareLabel: 'Del rapport',
      shareEnabled: 'Deling aktiveret',
      shareDisabled: 'Deling deaktiveret',
      copyLink: 'Kopiér link',
      linkCopied: 'Kopieret!',
      video: 'Videooptagelse',
      noVideo: 'Ingen videooptagelse tilgængelig.',
      videoLoading: 'Indlæser video...',
      clips: 'Kompetenceklip',
      noClips: 'Ingen klip tilgængelige endnu.',
      clipDuration: '{{sec}}s',
      reviewedBy: 'Gennemgået af',
      noReviewers: 'Ingen har gennemgået denne rapport endnu.',
      notes: 'Noter',
      noNotes: 'Ingen noter endnu.',
      addNote: 'Tilføj note',
      notePlaceholder: 'Skriv en note…',
      deleteNote: 'Slet',
      noteAdded: 'Note tilføjet.',
      aiDetection: 'AI-svarsdetektering',
      aiDetectionPending: 'Analyse i gang...',
      aiDetectionLow: 'Svar virker menneskelige',
      aiDetectionMedium: 'Nogle AI-lignende mønstre fundet',
      aiDetectionHigh: 'Svar sandsynligvis AI-genererede',
      aiDetectionScore: 'Sandsynlighedspoint',
      aiDetectionFlags: 'Markerede segmenter',
    },
    chat: {
      title: 'Spørg om kandidaten',
      placeholder: 'Stil et spørgsmål om kandidatens svar i interviewet...',
      inputPlaceholder: 'Skriv et spørgsmål...',
      send: 'Send',
      sending: 'Sender...',
      errorMessage: 'Der opstod en fejl. Prøv igen.',
    },
    orgChat: {
      title: 'Spørg om kandidater',
      placeholder:
        'Stil et spørgsmål om en hvilken som helst kandidat i alle stillinger...',
      inputPlaceholder: 'F.eks. Hvem fik højest point i problemløsning?',
      send: 'Send',
      sending: 'Sender...',
      errorMessage: 'Der opstod en fejl. Prøv igen.',
      toggle: 'AI-chat',
    },
    publicReport: {
      notFound: 'Denne rapport er ikke længere tilgængelig.',
      notFoundDescription:
        'Linket kan være udløbet, eller deling er deaktiveret.',
    },
    pdfExport: {
      downloadButton: 'Download',
      aggregatedReport: 'Download samlet rapport',
      transcripts: 'Download transskripter',
      individualReports: 'Download individuelle rapporter',
      drawerTitleTranscripts: 'Download transskripter',
      drawerTitleReports: 'Download individuelle rapporter',
      drawerDescription: 'Vælg kandidater til at inkludere i downloaden.',
      selectAll: 'Vælg alle',
      deselectAll: 'Fravælg alle',
      downloadSelected: 'Download valgte',
      downloadAll: 'Download alle',
      generating: 'Genererer {{completed}} af {{total}}...',
      complete: 'Download klar',
      error: 'Eksport mislykkedes. Prøv igen.',
      noCompletedCandidates: 'Ingen gennemførte interviews at eksportere.',
      cancel: 'Annuller',
    },
    aggregateReport: {
      noCompletedInterviews: 'Ingen afsluttede interviews endnu.',
      rank: 'Rangering',
      candidate: 'Kandidat',
      averageScore: 'Gennemsnitlig point',
      topN: 'Top {{n}}',
      competencies: 'Kompetencefordeling',
    },
    candidateFeedback: {
      title: 'Dine resultater',
      preparing: 'Din personlige tilbagemelding forberedes…',
      competencies: 'Kompetencepoint',
      scoreLabel: '{{score}} / 10',
    },
  },
  candidateSearch: {
    searchPlaceholder: 'Søg på navn eller e-mail…',
    filterJob: 'Alle stillinger',
    filterStatus: 'Alle statusser',
    filterMinScore: 'Minimumspoint',
    filterMaxScore: 'Maksimumspoint',
    filterFromDate: 'Fra dato',
    filterToDate: 'Til dato',
    clearFilters: 'Ryd filtre',
    noResults: 'Ingen kandidater fundet.',
    columnName: 'Navn',
    columnJob: 'Stilling',
    columnStatus: 'Status',
    columnScore: 'Gennemsnitlig point',
    columnDate: 'Inviteret',
    viewReport: 'Vis rapport',
    scoreValue: '{{score}}',
    noScore: '—',
  },
  notifications: {
    title: 'Notifikationer',
    markAllRead: 'Markér alle som læst',
    empty: 'Ingen notifikationer endnu.',
    typeInterviewCompleted: 'Interview afsluttet',
    typeInterviewFailed: 'Interview mislykkedes',
    typeLowBalance: 'Lav saldo',
    typeJobActivated: 'Stilling aktiveret',
    typeCandidateExpired: 'Kandidat udløbet',
  },
  dashboard: {
    getStarted: 'Kom i gang',
    getStartedDescription:
      'Udforsk oversigten og kom i gang med din første app.',
    tipChangeLanguage: 'TIP: Prøv at skifte sprog!',
    exploreDocumentation: 'Udforsk dokumentationen',
    statsInvited: 'Inviterede',
    statsCompleted: 'Afsluttede',
    statsAdvanced: 'Videresendte',
    statsAvgScore: 'Gennemsnitlig point',
    analytics: {
      title: 'Analyse',
      jobTitle: 'Stilling',
      status: 'Status',
      completionRate: 'Afslutningsrate',
    },
    accountLocked: {
      message:
        'Dit abonnement er udløbet. Genaktiver for at fortsætte med at afholde interviews.',
      reactivate: 'Genaktiver',
    },
  },
  settings: {
    avatar: {
      title: 'Din avatar',
      description: 'Dette er din avatar. Den vises på din profil.',
      hint: 'Klik på avataren for at uploade en brugerdefineret fra dine filer.',
      reset: 'Nulstil',
    },
    profile: {
      title: 'Din profil',
      description: 'Dit for- og efternavn vises for kandidater og kolleger.',
      firstNamePlaceholder: 'Fornavn',
      lastNamePlaceholder: 'Efternavn',
      hint: 'Brug dit rigtige navn.',
      save: 'Gem',
    },
    username: {
      title: 'Dit brugernavn',
      description: 'Dette er dit brugernavn. Det vises på din profil.',
      placeholder: 'Brugernavn',
      hint: 'Brug maksimalt 32 tegn.',
      save: 'Gem',
    },
    deleteAccount: {
      title: 'Slet konto',
      description:
        'Slet din konto permanent, alle dine projekter og alle relaterede data.',
      hint: 'Denne handling kan ikke fortrydes, fortsæt med forsigtighed.',
      confirm: 'Er du sikker?',
      delete: 'Slet konto',
    },
    contactSupport: {
      title: 'Kontakt support',
      description:
        'Rapporter en fejl, stil et spørgsmål eller anmod om en funktion.',
      topicLabel: 'Emne',
      topicBug: 'Fejl',
      topicTechnicalIssue: 'Teknisk problem',
      topicBilling: 'Fakturering',
      topicGeneral: 'Generelt spørgsmål',
      topicFeatureRequest: 'Funktionsanmodning',
      severityLabel: 'Alvorlighed',
      severityLow: 'Lav',
      severityMedium: 'Medium',
      severityHigh: 'Høj',
      severityCritical: 'Kritisk',
      subjectLabel: 'Emne',
      subjectPlaceholder: 'Kort beskrivelse af problemet',
      messageLabel: 'Besked',
      messagePlaceholder: 'Beskriv dit problem i detaljer…',
      submit: 'Send',
      submitting: 'Sender…',
      successTitle: 'Anmodning modtaget',
      successMessage: 'Din billet er sendt. Reference: {{ticketId}}',
      hint: 'Vi vender tilbage hurtigst muligt.',
    },
    tabs: {
      general: 'Generelt',
      billing: 'Fakturering',
      organisation: 'Organisation',
      branding: 'Branding',
      team: 'Team',
      notifications: 'Notifikationer',
    },
    branding: {
      title: 'Branding',
      description: 'Tilpas hvordan din organisation vises for kandidater.',
      logoTitle: 'Logo',
      logoDescription: 'Vises på kandidatens interviewinformationsskærm.',
      uploadLogo: 'Upload logo',
      uploading: 'Uploader...',
      primaryColorTitle: 'Primærfarve',
      primaryColorDescription:
        'Bruges på knapper og accenter i kandidatforløbet.',
      websiteTitle: 'Hjemmeside',
      websiteDescription:
        'Vises for kandidater som et link til at lære mere om dit firma.',
      taglineTitle: 'Slogan',
      taglineDescription:
        'En kort beskrivelse af dit firma, der vises på informationsskærmen.',
      taglinePlaceholder: 'F.eks. Vi bygger fremtidens arbejde',
      hint: 'Disse indstillinger er synlige for kandidater.',
      save: 'Gem',
      saving: 'Gemmer...',
      saved: 'Gemt',
    },
    notifications: {
      title: 'Notifikationsindstillinger',
      description: 'Vælg hvilke e-mailnotifikationer du ønsker at modtage.',
      lowBalanceLabel: 'Lav saldo',
      lowBalanceDescription:
        'Få en e-mail, når din saldo falder til 5 eller færre interviews.',
      interviewCompletedLabel: 'Interview afsluttet',
      interviewCompletedDescription:
        'Få en e-mail, når en kandidat afslutter sit interview.',
      reportReadyLabel: 'Rapport klar',
      reportReadyDescription:
        'Få en e-mail, når AI-analysen for et interview er klar.',
      teamMemberJoinedLabel: 'Teammedlem tilsluttet',
      teamMemberJoinedDescription:
        'Få en e-mail, når et nyt teammedlem tilslutter sig din organisation.',
    },
    team: {
      title: 'Teammedlemmer',
      description: 'Administrer dit teams adgang til organisationen.',
      inviteTitle: 'Invitér teammedlem',
      inviteDescription: 'Send en invitation via e-mail.',
      emailLabel: 'E-mail',
      emailPlaceholder: 'kollega@eksempel.dk',
      roleLabel: 'Rolle',
      roleMember: 'Medlem',
      roleAdmin: 'Admin',
      roleOwner: 'Ejer',
      invite: 'Invitér',
      inviteSent: 'Invitation sendt.',
      membersTitle: 'Nuværende medlemmer',
      remove: 'Fjern',
      removeDialogTitle: 'Fjern teammedlem',
      removeDialogDescription:
        'Er du sikker på, at du vil fjerne {{name}} fra organisationen?',
      removeConfirm: 'Fjern',
      cancel: 'Annuller',
      hint: 'Inviterede brugere får adgang til organisationens ressourcer.',
    },
    organisation: {
      title: 'Organisationsindstillinger',
      description: 'Indstillinger der vises for kandidater.',
      displayNameTitle: 'Visningsnavn',
      displayNameDescription:
        'Dette navn vises for kandidater i interviewportalen.',
      displayNamePlaceholder: 'Organisationsnavn',
      termsUrlTitle: 'Link til vilkår for brug',
      termsUrlDescription: 'Vises for kandidater ved interviewinvitation.',
      termsUrlPlaceholder: 'https://example.com/terms',
      privacyUrlTitle: 'Link til privatlivspolitik',
      privacyUrlDescription: 'Vises for kandidater ved interviewinvitation.',
      privacyUrlPlaceholder: 'https://example.com/privacy',
      hint: 'Disse indstillinger vises for kandidater.',
      save: 'Gem',
      sso: {
        title: 'Single Sign-On (SSO)',
        description:
          'Lad teammedlemmer logge ind med organisationens identitetsudbyder.',
        emailDomainTitle: 'E-maildomæne',
        emailDomainDescription:
          'E-maildomænet der bruges til at identificere SSO-brugere (f.eks. firma.dk).',
        emailDomainPlaceholder: 'firma.dk',
        discoveryUrlTitle: 'OIDC Discovery-URL',
        discoveryUrlDescription:
          'URL-en til OpenID Connect-konfigurationen hos din identitetsudbyder.',
        discoveryUrlPlaceholder:
          'https://din-idp.example.com/.well-known/openid-configuration',
        clientIdTitle: 'Klient-ID',
        clientIdDescription:
          'Klient-ID-et der er registreret hos identitetsudbyderen.',
        clientIdPlaceholder: 'klient-id',
        clientSecretTitle: 'Klienthemmelighed',
        clientSecretDescription:
          'Klienthemmeligheden der er registreret hos identitetsudbyderen.',
        clientSecretPlaceholder: '••••••••',
        clientSecretSetOn: 'Sat den {{date}}',
        clientSecretRotate: 'Rotér',
        clientSecretRemove: 'Fjern',
        saveConfig: 'Gem SSO-konfiguration',
        enforceTitle: 'Kræv SSO',
        enforceDescription:
          'Kræv at teammedlemmer logger ind via SSO. Organisationsejere er altid undtaget.',
        enforced: 'Aktiveret',
        notEnforced: 'Ikke aktiveret',
        hint: 'SSO-konfigurationen er kun synlig for organisationsejere.',
      },
    },
  },
  support: {
    contactForm: {
      nameLabel: 'Navn',
      namePlaceholder: 'Dit navn',
      emailLabel: 'E-mail',
      emailPlaceholder: 'din@email.dk',
      successTitle: 'Besked sendt',
      successMessage:
        'Vi har modtaget din besked og vender tilbage hurtigst muligt.',
      errorGeneric: 'Noget gik galt. Prøv igen.',
    },
  },
  billing: {
    balanceTitle: 'Din saldo',
    balanceDescription: 'Tilgængelig saldo til interviews.',
    subscriptionTitle: 'Abonnement',
    subscriptionDescription:
      'Start din 14-dages gratis prøveperiode. Ingen gebyr, før prøveperioden er slut.',
    subscriptionNote: 'Du omdirigeres til Polar for at fuldføre tilmeldingen.',
    subscriptionStatusActive: 'Aktiv',
    subscriptionStatusTrialing: 'Prøveperiode',
    subscribeLabel: 'Start gratis prøveperiode',
    manageSubscription: 'Administrer abonnement',
    trialEndsOn: 'Prøveperioden slutter {{date}}',
    topUpTitle: 'Påfyld saldo',
    topUpDescription: 'Vælg et beløb for at påfylde din konto.',
    topUpNote: 'Du omdirigeres til Polar for betaling.',
    topUpLabel: 'Påfyld',
    invoiceHistoryTitle: 'Fakturahistorik',
    invoiceHistoryDescription: 'Se dine tidligere køb og fakturaer.',
    redirectedToPolar: 'Du omdirigeres til Polar kundeportal.',
    viewInvoices: 'Vis fakturaer',
    billingEmailTitle: 'Fakturadresse',
    billingEmailDescription:
      'Kvitteringer og fakturaer fra Polar sendes til denne adresse.',
    billingEmailPlaceholder: 'faktura@eksempel.dk',
    billingEmailSave: 'Gem',
    billingEmailSaved: 'Gemt.',
    billingEmailHint:
      'Lad stå tomt for at bruge kontoens e-mailadresse. Kun admins og ejere kan ændre dette.',
    autoTopUpTitle: 'Automatisk påfyldning',
    autoTopUpDescription:
      'Påfyld automatisk din saldo, når den falder under en tærskel.',
    autoTopUpEnabled: 'Aktiveret',
    autoTopUpDisabled: 'Deaktiveret',
    autoTopUpNoPaymentMethod:
      'Ingen gemt betalingsmetode. Gennemfør et køb først for at aktivere automatisk påfyldning.',
    autoTopUpThresholdLabel: 'Påfyld når færre end',
    autoTopUpThresholdUnit: 'interviews tilbage',
    autoTopUpProductLabel: 'Påfyldningspakke',
    autoTopUpSave: 'Gem',
    autoTopUpSaved: 'Gemt.',
  },
  checkout: {
    title: 'Betaling gennemført',
    description: 'Din betaling er gennemført, og din saldo er opdateret.',
    completed: 'Købet fuldført!',
    returnToDashboard: 'Gå tilbage til oversigten',
  },
  templates: {
    empty: 'Ingen skabeloner endnu',
    emptyDescription:
      'Gem et eksisterende job som skabelon for at genbruge det.',
    questionCount: 'Spørgsmål',
    screenerCount: 'Screener',
    interviewCount: 'Interviews',
    useTemplate: 'Brug skabelon',
    saveAsTemplate: 'Gem som skabelon',
    saveAsTemplateTitle: 'Gem som skabelon',
    templateNameLabel: 'Skabelonnavn',
    templateNamePlaceholder: 'F.eks. Senior Backend-udvikler',
    saveButton: 'Gem',
    createFromTemplateTitle: 'Opret stilling fra "{{name}}"',
    createJobButton: 'Opret stilling',
  },
  candidate: {
    loading: 'Indlæser…',
    invalid: {
      title: 'Linket er ugyldigt',
      body: 'Dette invitationslink er udløbet eller er ikke længere gyldigt.',
    },
    orgLocked: {
      title: 'Kontoen er inaktiv',
      body: 'Denne organisations konto er i øjeblikket inaktiv. Kontakt rekruttereren for hjælp.',
    },
    disqualified: {
      title: 'Tak for din interesse',
      body: 'Desværre opfylder du ikke kravene til denne stilling.',
    },
    screener: {
      title: 'Forhåndstilmeldingsspørgsmål',
      continue: 'Fortsæt',
    },
    additionalInfo: {
      title: 'Yderligere information',
      continue: 'Fortsæt',
    },
    briefing: {
      howToConduct: 'Hvordan ønsker du at gennemføre interviewet?',
      usesModality: 'Dette interview bruger',
      aboutToStart: 'Du er ved at starte et AI-ledet interview.',
      expectedDuration: 'Forventet varighed: cirka {{minutes}} minutter.',
      quietEnvironment:
        'Sørg for, at du befinder dig i et roligt miljø med en fungerende mikrofon.',
      aiNotice:
        'Dine svar vurderes af et AI-system. En menneskelig rekrutterer gennemgår alle AI-genererede resultater og træffer hver ansættelsesbeslutning selvstændigt – ingen automatiseret beslutning træffes om din ansøgning.',
      requestScores:
        'Du kan til enhver tid anmode om dine kompetencepoint og begrundelser via e-mail til',
      consentRecording:
        'Jeg samtykker til at blive optaget under dette interview.',
      consentAi:
        'Jeg samtykker til, at mine svar behandles af AI under dette interview.',
      termsOfService: 'Vilkår for brug',
      privacyPolicy: 'Privatlivspolitik',
      starting: 'Starter…',
      ready: 'Jeg er klar',
      insufficientBalance:
        'Interviewet kunne ikke startes lige nu. Kontakt rekruttereren.',
      orgLockedError:
        'Denne organisations konto er i øjeblikket inaktiv. Kontakt rekruttereren.',
    },
    modality: {
      video: { label: 'Video', description: 'Kamera og mikrofon' },
      audio: { label: 'Kun lyd', description: 'Mikrofon' },
      chat: { label: 'Tekst', description: 'Skriftlige svar' },
    },
    room: {
      connecting: 'Intervieweren forbinder…',
      questionOf: 'Spørgsmål {{index}} af {{total}}',
      answered: '{{completed}} af {{total}} besvaret',
      chatPlaceholder: 'Skriv dit svar…',
      chatSend: 'Send',
      chatLeave: 'Forlad',
    },
    completed: {
      title: 'Interviewet er afsluttet',
      defaultOutro: 'Tak for din tid. Vi vender tilbage snart.',
      feedbackQuestion: 'Hvordan var din oplevelse?',
      feedbackComment: 'Yderligere kommentarer? (valgfrit)',
      feedbackSubmit: 'Send tilbagemelding',
      feedbackThanks: 'Tak for din tilbagemelding.',
    },
    abandoned: {
      title: 'Interviewet afsluttet',
      body: 'Du har forladt interviewet. Hvis det var med vilje, behøver du ikke gøre noget mere.',
    },
    error: {
      title: 'Noget gik galt',
      body: 'Der opstod en uventet fejl under dit interview. Prøv igen eller kontakt support.',
      reportLink: 'Rapporter et teknisk problem',
      severityTitle: 'Hvor alvorligt var problemet?',
      severityLow: 'Mindre – jeg kunne fortsætte',
      severityMedium: 'Betydeligt – interviewet blev forstyrret',
      severityHigh: 'Kritisk – jeg kunne ikke fuldføre interviewet',
      describeTitle: 'Beskriv hvad der skete',
      describePlaceholder: 'F.eks. Lyden holdt op med at virke, siden frøs…',
      submit: 'Send rapport',
      submitting: 'Sender…',
      submitted: 'Din rapport er sendt. Tak.',
    },
  },
  ai: {
    generate: 'Generer',
  },
}

export default da
